import type { LayoutNode } from "@imminently/decisively-core";
import type { GraphNode } from "@packages/commons";
import { cloneDeep, set } from "lodash";
import pako from 'pako';
import { eigenvectorCentrality } from "./math";
import {
  DecisionGraph,
  type StoredRuleGraph,
} from "@imminently/decisively-core";
import {
  type VisibleMask,
  graphNodeByKey,
  shownIds,
  shownPredecessors,
  shownSuccessors,
} from "@/components/GraphVisualisation/decisionGraphView";

/**
 * TODO want a better name for this.
 * George suggested AttributeType, but attribute.type is the ValueType, which is confusing.
 * Any suggestion would be appreciated.
 * Also suggested AttributeCategory, but that conflicts with attribute.category
 */
export enum GraphNodeType {
  GOAL = "goal",
  INPUT = "input",
  DERIVED = "derived",
  IDENTIFIER = "identifier",
  MISSING = "waiting_for_save",
  VALIDATION = "validation",
};

export type GraphLibNode = LayoutNode<GraphNode>;
export interface GraphNodeWithType extends GraphNode { nodeType: GraphNodeType };

export type NodeGroups = {
  /**
   * - It has no parents (predecessors)
   * - It is not an identifier (identifier: true in node)
   * - It is not a foreign key (fk: true in node)
   * - It can be on any entity
   */
  goals: GraphLibNode[];
  /**
   * - has no successors
   * - has no "condtions", nor "rows"
   */
  inputs: GraphLibNode[];
  /** node is an "identifier node" if it has "identifier: true" */
  identifiers: GraphLibNode[];
  /** neither a "goal", nor an "input" */
  derived: GraphLibNode[];
}

export const getNodeType = (node: GraphNode | null | undefined) => {
  // if(!node) throw "Cannot get node type! Node not found in graph.";
  // does not exist yet, so return null
  if (!node) return GraphNodeType.MISSING;

  if (node.validation) {
    return GraphNodeType.VALIDATION;
  }

  if (node.goal && !node.fk && !node.identifier) {
    return GraphNodeType.GOAL;
  }

  if (node.identifier) {
    return GraphNodeType.IDENTIFIER;
  }

  if (!node.definedIn) {
    return GraphNodeType.INPUT;
  }

  return GraphNodeType.DERIVED;
};

export type GraphCounts = {
  total: number;
  input: number;
  derived: number;
  goals: number;
  identifiers: number;
};

/** A graph in any form (a DecisionGraph is used as it is; others are read once per object). */
type AnyGraph = DecisionGraph | StoredRuleGraph | Record<string, any>;

/**
 * Plain arrays, not an immer draft: immer auto-freezes whatever is pushed into a draft, and these are the graph's own
 * live node objects (freezing them would break later in-place edits through the graph).
 */
export const groupGraphNodes = (graph: AnyGraph, entity?: string): NodeGroups => {
  const g = DecisionGraph.from(graph as any);
  const groups: NodeGroups = { goals: [], inputs: [], derived: [], identifiers: [] };
  for (const id of g.ids()) {
    if (entity !== undefined && g.field(id, "entity") !== entity) continue;
    if (g.field(id, "fk")) continue;
    const node = g.node(id) as GraphLibNode;
    const identifier = g.field(id, "identifier");

    if (g.predecessors(id).length === 0 && !identifier) {
      groups.goals.push(node);
      continue;
    }

    if (identifier) {
      groups.identifiers.push(node);
      continue;
    }

    if (g.successors(id).length === 0 && !g.field(id, "conditions") && !g.field(id, "rows")) {
      groups.inputs.push(node);
      continue;
    }

    groups.derived.push(node);
  }
  return groups;
};

export const nodeGroupCount = (groups: NodeGroups): GraphCounts => ({
  total: groups.goals.length + groups.inputs.length + groups.derived.length + groups.identifiers.length,
  goals: groups.goals.length,
  input: groups.inputs.length,
  derived: groups.derived.length,
  identifiers: groups.identifiers.length,
});

// ===================================================================================

/**
 * Flat list of graph nodes usable as interview-designer "model inputs": every node that has a
 * description, excluding internal fk attributes, each tagged with a `graph_type` (via
 * `getNodeType`) for consumers that key off it (e.g. GraphAttribute's derived/goal dot).
 *
 * Optionally scoped to the nodes reachable from `goalId` (root/descendants), same as the old
 * `getModelInputsAll`'s goal filtering; pass no goal to get every node in the graph.
 *
 * Replaces `getModelInputsAll` (apps/portal/src/pages/models/shared/getModelInputs.js), which
 * built this same list via `lodash.set(model, \`${entity}.fields.${nodeId}\`, cfg)`. That breaks
 * for any canonical (description-derived) node id containing a "." - lodash re-parses the
 * template string as a nested path instead of treating the id as one literal key, so the node
 * silently lands somewhere else in the object and never makes it into the flattened list.
 */
export const getModelInputs = (graph: AnyGraph | null | undefined, goalId?: string | null): GraphNode[] => {
  if (!graph) return [];

  const g = DecisionGraph.from(graph as any);

  let scopedIds: number[] | null = null;
  if (goalId) {
    const goal = g.ref(goalId);
    if (goal === undefined) console.error("Error fetching children of goal", `Graph does not have node: ${goalId}`);
    else scopedIds = g.preorder(goal);
  }

  const nodes: GraphNode[] = [];
  for (const id of scopedIds ?? g.ids()) {
    if (!g.isListed(id) || !g.field(id, "description") || g.field(id, "fk")) continue;
    const node = cloneDeep(g.node(id)!.toJSON()) as GraphNode;
    nodes.push({ ...node, graph_type: getNodeType(node) });
  }
  return nodes;
};

/** The paths of the input nodes (see groupGraphNodes) reachable from the goal, in depth-first order. */
export const gatherInputNodesByGoalId = (graph: AnyGraph | null, goalId: string): string[] => {

  if (graph === null) {
    return [];
  }
  const g = DecisionGraph.from(graph as any);
  const goal = g.ref(goalId);
  if (goal === undefined) return [];

  const inputIds = new Set<string>();
  for (const node of groupGraphNodes(g).inputs) inputIds.add(node.id);

  return g.preorder(goal).map((id) => g.deref(id)).filter((path) => inputIds.has(path));
};

// Graph category code ported from decisively audit (utils/GraphFunc.ts)

/**
 *
 *
 const exampleData = {
    "123": {
      "456": {}
    },
    "789": {},
    "111": {}
  }
 *
 * given data structure like example data, write a function that converts it to an array of objects with children
 */
const convertData = (data: Record<string, any>, transform: (key: string) => any): any[] => {
  const result: any[] = [];
  for (const key in data) {
    const obj = { id: key, ...transform(key), children: convertData(data[key], transform) };
    result.push(obj);
  }
  return result;
};

/**
 * Builds an entity path map from category nodes.
 * Groups nodes by their entity path (the path without the attribute name).
 *
 * Example output:
 * {
 *   "entity/1": [node1],
 *   "entity/1/sub/1/other/2": [node2, node3]
 * }
 */
export const buildEntityPathMap = (categoryNodes: GraphNode[]): Map<string, GraphNode[]> => {
  const entityPathMap = new Map<string, GraphNode[]>();

  categoryNodes.forEach((node) => {
    const path = node.path || node.id;
    const pathSegments = path.split("/");
    const entityPath = pathSegments.slice(0, -1).join("/");

    if (!entityPathMap.has(entityPath)) {
      entityPathMap.set(entityPath, []);
    }
    entityPathMap.get(entityPath)!.push(node);
  });

  return entityPathMap;
};

/**
 * Finds the identifier node for a given entity path from the entity path map.
 * Returns the identifier node if found, null otherwise.
 */
export const findIdentifierForEntityPath = (
  entityPath: string,
  entityPathMap: Map<string, GraphNode[]>
): GraphNode | null => {
  const nodes = entityPathMap.get(entityPath);
  if (!nodes) return null;
  return nodes.find((n) => n.identifier) || null;
};

export const getCategories = (goal: string, graph: AnyGraph) => {
  const g = DecisionGraph.from(graph as any);
  // First, collect only category nodes (not identifiers by default)
  const categoryNodes: GraphNode[] = [];
  const seen = new Set<string>();
  const nodeAt = (id: number) => g.node(id) as GraphNode | undefined;

  // the goal and everything below it, depth first, by the nodes' own path (or id) as the graph's keys were
  const collectCategories = (start: number) => {
    const stack = [start];
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (!g.isListed(id) || g.field(id, "hidden")) continue;
      const nodeKey = (g.field(id, "path") || g.field(id, "id")) as string;
      if (seen.has(nodeKey)) continue;
      seen.add(nodeKey);

      // Only include category nodes (not identifiers)
      if (g.field(id, "category")) categoryNodes.push(nodeAt(id)!);

      // successors of the node at its own key (path or id)
      const keyed = g.ref(nodeKey) ?? id;
      const children = g.successors(keyed);
      for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
    }
  };


  // one pass over the graph's nodes: identifier categories, and the first categoryTitle / identifier node at each
  // entity path (the grouping candidates looked up per path below)
  const firstCategoryTitleAt = new Map<string, GraphNode>();
  const firstIdentifierAt = new Map<string, GraphNode>();
  for (const id of g.ids()) {
    const identifier = g.field(id, "identifier");
    const categoryTitle = g.field(id, "categoryTitle");
    if (identifier && g.field(id, "category") && !g.field(id, "hidden")) {
      categoryNodes.push(nodeAt(id)!);
    }
    if (categoryTitle || identifier) {
      const nodeEntityPath = ((g.field(id, "path") || g.field(id, "id")) as string).split("/").slice(0, -1).join("/");
      if (categoryTitle && !firstCategoryTitleAt.has(nodeEntityPath)) firstCategoryTitleAt.set(nodeEntityPath, nodeAt(id)!);
      if (identifier && !firstIdentifierAt.has(nodeEntityPath)) firstIdentifierAt.set(nodeEntityPath, nodeAt(id)!);
    }
  }

  // TODO: handle description and public name
  const goalId = g.ref(goal);
  if (goalId !== undefined) collectCategories(goalId);

  // Build entity path map from category nodes only
  const entityPathMap = buildEntityPathMap(categoryNodes);

  // Find entity paths that have multiple category nodes - these need an identifier as parent
  const entityPathsNeedingIdentifier: string[] = [];
  entityPathMap.forEach((nodes, entityPath) => {
    if (nodes.length > 1) {
      entityPathsNeedingIdentifier.push(entityPath);
    }
  });


  // For entity paths with multiple category nodes, find the grouping node
  // Priority: 1) categoryTitle node, 2) identifier node
  const groupingNodesByEntityPath = new Map<string, GraphNode>();
  entityPathsNeedingIdentifier.forEach((entityPath) => {
    const nodesAtPath = entityPathMap.get(entityPath) || [];

    // First: check if any category node at this path has categoryTitle
    const categoryTitleNode = nodesAtPath.find((n) => n.categoryTitle);
    if (categoryTitleNode) {
      groupingNodesByEntityPath.set(entityPath, categoryTitleNode);
      return;
    }

    // Second: look for a pure categoryTitle node in the graph (not a category itself)
    const pureCategoryTitleNode = firstCategoryTitleAt.get(entityPath);
    if (pureCategoryTitleNode) {
      groupingNodesByEntityPath.set(entityPath, pureCategoryTitleNode);
      return;
    }

    // Third: check if any category node at this path is also an identifier
    const categoryIdentifier = nodesAtPath.find((n) => n.identifier);
    if (categoryIdentifier) {
      groupingNodesByEntityPath.set(entityPath, categoryIdentifier);
      return;
    }

    // Fourth: look for a pure identifier node in the graph
    const pureIdentifierNode = firstIdentifierAt.get(entityPath);

    if (pureIdentifierNode) {
      groupingNodesByEntityPath.set(entityPath, pureIdentifierNode);
    }
  });

  // Collect grouping nodes that need to be added (only pure grouping nodes, not category nodes)
  const groupingNodesToAdd: GraphNode[] = [];
  groupingNodesByEntityPath.forEach((groupingNode) => {
    // Only add if it's not already in categoryNodes
    if (!groupingNode.category) {
      groupingNodesToAdd.push(groupingNode);
    }
  });

  // Add grouping nodes to the entity path map
  groupingNodesToAdd.forEach((node) => {
    const nodePath = node.path || node.id;
    const entityPath = nodePath.split("/").slice(0, -1).join("/");
    if (!entityPathMap.has(entityPath)) {
      entityPathMap.set(entityPath, []);
    }
    entityPathMap.get(entityPath)!.push(node);
  });

  // Sort entity paths by length (shorter paths = parents, should be processed first)
  const sortedEntityPaths = Array.from(entityPathMap.keys()).sort((a, b) => {
    const lenA = a.split("/").length;
    const lenB = b.split("/").length;
    if (lenA !== lenB) return lenA - lenB;
    return a.localeCompare(b);
  });

  // Build the tree structure
  const entityPathToTreeNode = new Map<string, GraphNodeWithChildren>();
  const rootNodes: GraphNodeWithChildren[] = [];

  sortedEntityPaths.forEach((entityPath) => {
    const nodesAtPath = entityPathMap.get(entityPath) || [];

    // Find parent entity path (longest prefix match)
    let parentEntityPath: string | null = null;
    for (let i = sortedEntityPaths.indexOf(entityPath) - 1; i >= 0; i--) {
      const candidatePath = sortedEntityPaths[i];
      if (entityPath.startsWith(candidatePath + "/")) {
        parentEntityPath = candidatePath;
        break;
      }
    }

    // Get parent tree node if exists
    const parentTreeNode = parentEntityPath ? entityPathToTreeNode.get(parentEntityPath) : null;

    // Check if there's a grouping node for this path (meaning >1 nodes need wrapping)
    const groupingNode = groupingNodesByEntityPath.get(entityPath);

    if (nodesAtPath.length > 1 && groupingNode) {
      // More than 1 node: wrap them in the grouping node
      const groupingTreeNode: GraphNodeWithChildren = {
        ...groupingNode,
        children: [],
      };

      // Set as representative for this entity path
      entityPathToTreeNode.set(entityPath, groupingTreeNode);

      // Add grouping node to parent or root
      if (parentTreeNode) {
        parentTreeNode.children.push(groupingTreeNode);
      } else {
        rootNodes.push(groupingTreeNode);
      }

      // Add other nodes as children of the grouping node
      nodesAtPath.forEach((node) => {
        if (node !== groupingNode) {
          const childTreeNode: GraphNodeWithChildren = {
            ...node,
            children: [],
          };
          groupingTreeNode.children.push(childTreeNode);
        }
      });
    } else {
      // Single node (or no grouping node found): add directly
      nodesAtPath.forEach((node, index) => {
        const treeNode: GraphNodeWithChildren = {
          ...node,
          children: [],
        };

        // First node becomes representative for this entity path
        if (index === 0) {
          entityPathToTreeNode.set(entityPath, treeNode);
        }

        // Add to parent or root
        if (parentTreeNode) {
          parentTreeNode.children.push(treeNode);
        } else {
          rootNodes.push(treeNode);
        }
      });
    }
  });

  return rootNodes;
};

export type GraphNodeWithChildren = GraphNode & { children: GraphNodeWithChildren[] };

export const uncompressGraph = (compressed: any) => {
  try {
    const c = pako.inflate(compressed, { to: 'string' });
    return JSON.parse(c);
  } catch (error) {
    console.log('Failed to uncompress graph', error);
  }
};


/**
 * Counts of the view's attributes by kind, and its most influential inputs (by eigenvector centrality over the rule
 * nodes): read over ids and the packed graph, only the top inputs are made into node objects.
 */
export const getGraphStatistics = (graph: DecisionGraph | null | undefined, visible: VisibleMask = null, debug: boolean = false) => {
  const counts: GraphCounts = {
    total: 0,
    input: 0,
    derived: 0,
    goals: 0,
    identifiers: 0,
  };
  const interestingNodes: any[] = [];

  if (!graph) return { counts, interestingNodes };

  const fks = graph.templateField("fk");
  const values = graph.templateField("value");
  const conditions = graph.templateField("conditions");
  const rows = graph.templateField("rows");
  const isSink = (id: number) => shownSuccessors(graph, visible, id).length === 0;

  for (const id of shownIds(graph, visible)) {
    const t = graph.template(id);
    if (fks[t]) continue;
    if (debug && graph.field(id, "hidden")) continue;
    if (isSink(id)) {
      // Some inputs are actually derived - if they have a value
      if (values[t] || conditions[t] || rows[t]) counts.derived = counts.derived + 1;
      else counts.input = counts.input + 1;
    } else if (shownPredecessors(graph, visible, id).length === 0) {
      counts.goals = counts.goals + 1;
    } else {
      counts.derived = counts.derived + 1;
    }
    counts.total = counts.total + 1; // always increase total
  }

  // centrality over the rule nodes (not instances, not foreign keys), along edges either way
  const members: number[] = [];
  const position = new Int32Array(graph.idBound()).fill(-1);
  for (const id of shownIds(graph, visible)) {
    if (!graph.isGlobal(id) || fks[graph.template(id)]) continue;
    position[id] = members.length;
    members.push(id);
  }
  const offsets = new Uint32Array(members.length + 1);
  const lists: number[][] = members.map((id, n) => {
    const list: number[] = [];
    for (const to of graph.successors(id)) if (position[to] >= 0) list.push(position[to]);
    for (const from of graph.predecessors(id)) if (position[from] >= 0) list.push(position[from]);
    offsets[n + 1] = offsets[n] + list.length;
    return list;
  });
  const neighbours = new Uint32Array(offsets[members.length]);
  lists.forEach((list, n) => neighbours.set(list, offsets[n]));
  const eigens = eigenvectorCentrality(members.length, offsets, neighbours);

  // the top 10 inputs
  const order = members.map((_, n) => n).sort((a, b) => eigens[b] - eigens[a]);
  for (const n of order) {
    if (interestingNodes.length >= 10) break;
    const id = members[n];
    if (!isSink(id)) continue;
    const node = graph.node(id);
    if (node) interestingNodes.push(node);
  }

  return { counts, interestingNodes };
};

export const jsonPayloadToDescriptions = (payload, graph) => {
  // change this to use the descriptions in the graph instead of GUIDs
  try {
    let output = typeof payload === "string" ? JSON.parse(payload) : payload;
    const convertValue = (value) => {
      if (Array.isArray(value)) {
        return value.map(convertValue);
      }
      if (value && Object.prototype.toString.call(value) === "[object Object]") {
        const newValue = {};
        recurse(Object.entries(value), [], newValue);
        return newValue;
      }
      return value;
    };
    const recurse = (attributes, path, newData) => {
      let dupes: any = [];
      for (const a of attributes) {
        if (a[0] === "@id") {
          set(newData, [...path, "@id"], a[1]);
          continue;
        }

        let label = a[0];
        let node: any = graphNodeByKey(graph, a[0]);
        if (node) {
          label = node.description;
          if (dupes.indexOf(node.description) !== -1) {
            // An attribute of this name was already used, this means it's in a name space
            // Use the file path to this attribute instead
            label = `${node.definedIn}/${node.description}`;
          } else dupes.push(node.description);
        }

        if (Array.isArray(a[1])) {
          set(newData, [...path, label], a[1].map(convertValue));
        } else {
          set(newData, [...path, label], convertValue(a[1]));
        }
      }
    };
    let newData = {};
    recurse(Object.entries(output.data), [], newData);
    return JSON.stringify({ data: newData }, null, 2);
  } catch (error) {
    console.log("Error converting value to graph descriptions", error);
    // Do nothing - revert to using the string provided
  }
  return payload;
};
